import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { FlaskConical, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import FileUpload from "@/components/FileUpload";

const STATUSES = ["requested", "sample_collected", "in_progress", "ready", "approved", "delivered"];
const STATUS_COLOR = {
  requested: "bg-muted text-muted-foreground",
  sample_collected: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))]",
  in_progress: "bg-accent/20 text-accent-foreground",
  ready: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  approved: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  delivered: "bg-primary/10 text-primary",
};

export default function LaboratoryPage() {
  const { user } = useAuth();
  const [labs, setLabs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tests: "", patient_id: "", notes: "", priority: "normal" });
  const [resultOpen, setResultOpen] = useState(null);
  const [resultText, setResultText] = useState("");

  const load = useCallback(async () => {
    const [l, p] = await Promise.all([api.get("/lab-requests"), api.get("/patients")]);
    setLabs(l.data); setPatients(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/lab-requests", {
        patient_id: form.patient_id,
        tests: form.tests.split(",").map((t) => t.trim()).filter(Boolean),
        notes: form.notes, priority: form.priority,
      });
      toast.success("Lab request created");
      setOpen(false); setForm({ tests: "", patient_id: "", notes: "", priority: "normal" });
      load();
    } catch { toast.error("Failed"); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/lab-requests/${id}/status?status=${status}`);
    load();
  };

  const submitResult = async (e) => {
    e.preventDefault();
    await api.put(`/lab-requests/${resultOpen}/results`, { results: resultText });
    toast.success("Results recorded");
    setResultOpen(null); setResultText("");
    load();
  };

  return (
    <div className="space-y-5 fade-up" data-testid="lab-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Laboratory</h1>
          <p className="text-sm text-muted-foreground mt-1">{labs.length} lab requests</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-lab-btn"><Plus className="size-4" /> New request</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New lab request</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1.5"><Label>Patient *</Label>
                <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Tests (comma-separated) *</Label>
                <Input value={form.tests} onChange={(e) => setForm({ ...form, tests: e.target.value })} placeholder="CBC, Glucose, Liver function" required /></div>
              <div className="space-y-1.5"><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["normal", "urgent", "stat"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              {form.patient_id ? (
                <div className="border-t border-border pt-3">
                  <FileUpload patientId={form.patient_id} resourceType="lab_request" label="Attach files (referral, prior results...)" compact />
                </div>
              ) : (
                <div className="border-t border-border pt-3 text-[11px] text-muted-foreground">
                  Select a patient to attach files (referrals, prior results...).
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-lab-btn">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="py-3 px-5">Patient</th><th>Tests</th><th>Priority</th><th>Status</th><th className="px-5"></th>
            </tr></thead>
            <tbody>
              {labs.map((l) => {
                const p = patients.find((x) => x.id === l.patient_id);
                return (
                  <tr key={l.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                    <td className="py-3 px-5 font-medium">{p?.name || "—"}</td>
                    <td className="text-muted-foreground">{l.tests?.join(", ")}</td>
                    <td><Badge variant="outline" className={l.priority === "stat" ? "border-destructive text-destructive" : ""}>{l.priority}</Badge></td>
                    <td><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[l.status] || ""}`}>{l.status}</span></td>
                    <td className="px-5 py-3 text-right space-x-2">
                      {l.status !== "ready" && (user.role === "lab_tech" || user.role === "super_admin") && (
                        <Button size="sm" variant="outline" onClick={() => setResultOpen(l.id)} data-testid={`lab-result-${l.id}`}>Add results</Button>
                      )}
                      <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                        <SelectTrigger className="w-40 h-8 inline-flex"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
              {labs.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-muted-foreground">No lab requests.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!resultOpen} onOpenChange={(o) => !o && setResultOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add lab results</DialogTitle></DialogHeader>
          <form onSubmit={submitResult} className="space-y-3">
            <Textarea value={resultText} onChange={(e) => setResultText(e.target.value)} rows={8} required placeholder="Enter results / values..." />
            {resultOpen && (() => {
              const lab = labs.find((x) => x.id === resultOpen);
              return lab ? <FileUpload patientId={lab.patient_id} resourceType="lab_request" resourceId={resultOpen} label="Attach result files (PDF, images)" compact /> : null;
            })()}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResultOpen(null)}>Cancel</Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
